import type {
  BuiltInParameterKey,
  ImportedCurveColumn,
  ImportedCurveRow,
  SweetSpotCurveField,
} from './types';
import { suggestUnitConversion, TARGET_UNITS } from './sweetSpotUnits';

type CurveIdentity = {
  canonical: string;
  displayName: string;
  description: string;
  evaluationField?: SweetSpotCurveField;
};

const CURVE_IDENTITIES: CurveIdentity[] = [
  { canonical: 'DEPTH', displayName: 'Depth（测深）', description: '测量深度', evaluationField: 'depth' },
  { canonical: 'TOC', displayName: 'TOC（总有机碳）', description: '总有机碳含量', evaluationField: 'toc' },
  { canonical: 'POROSITY', displayName: '孔隙度', description: '储层孔隙度', evaluationField: 'porosity' },
  { canonical: 'BRITTLENESS', displayName: 'BI（脆性指数）', description: '岩石脆性指数', evaluationField: 'brittleness' },
  { canonical: 'CLAY', displayName: '黏土含量', description: '黏土或泥质含量', evaluationField: 'clayContent' },
  { canonical: 'YOUNG', displayName: 'YM（杨氏模量）', description: '动态或静态杨氏模量', evaluationField: 'youngModulus' },
  { canonical: 'POISSON', displayName: 'PR（泊松比）', description: '泊松比', evaluationField: 'poissonRatio' },
  { canonical: 'SHMIN', displayName: 'Shmin（最小水平主应力）', description: '最小水平主应力', evaluationField: 'minHorizontalStress' },
  { canonical: 'GR', displayName: 'GR（自然伽马）', description: '自然伽马曲线' },
  { canonical: 'RT', displayName: 'RT（电阻率）', description: '深电阻率曲线' },
  { canonical: 'RD', displayName: 'RD（深电阻率）', description: '深探测电阻率曲线' },
  { canonical: 'RS', displayName: 'RS（浅电阻率）', description: '浅探测电阻率曲线' },
  { canonical: 'AC', displayName: 'AC（声波时差）', description: '声波时差曲线' },
  { canonical: 'DEN', displayName: 'DEN（密度）', description: '体积密度曲线' },
  { canonical: 'CNL', displayName: 'CNL（中子孔隙度）', description: '补偿中子曲线' },
  { canonical: 'CAL', displayName: 'CAL（井径）', description: '井径曲线' },
  { canonical: 'SP', displayName: 'SP（自然电位）', description: '自然电位曲线' },
  { canonical: 'SW', displayName: 'SW（含水饱和度）', description: '含水饱和度曲线' },
];

const ALIASES: Record<string, string[]> = {
  DEPTH: ['DEPT', 'DEPTH', 'MD', 'MEASUREDDEPTH', 'DEP', '测深'],
  TOC: ['TOC', 'TOTALORGANICCARBON', '有机碳', '总有机碳'],
  POROSITY: ['PHI', 'PHIE', 'PHIT', 'POR', 'PORO', 'POROSITY', '孔隙度'],
  BRITTLENESS: ['BI', 'BRIT', 'BRITTLENESS', 'BRITTLENESSINDEX', '脆性指数'],
  CLAY: ['VSH', 'VCL', 'VCLAY', 'CLAY', 'CLAYCONTENT', 'CLAY_CONTENT', '泥质含量', '黏土含量'],
  YOUNG: ['YM', 'YME', 'YOUNG', 'YOUNGMODULUS', 'E', 'DYM', '杨氏模量'],
  POISSON: ['PR', 'POIS', 'POISSON', 'POISSONRATIO', 'NU', '泊松比'],
  SHMIN: ['SHMIN', 'SH_MIN', 'HMIN', 'MINHORIZONTALSTRESS', 'MIN_HORIZONTAL_STRESS', '最小水平主应力'],
  GR: ['GR', 'GAMMARAY'], RT: ['RT', 'RES', 'DEEPRESISTIVITY'], RD: ['RD'], RS: ['RS'],
  AC: ['AC', 'DT', 'SONIC'], DEN: ['DEN', 'RHOB', 'DENSITY'], CNL: ['CNL', 'NPHI'],
  CAL: ['CAL', 'CALI', 'CALIPER'], SP: ['SP'], SW: ['SW', 'WATERSATURATION'],
};

export function normalizeCurveName(value: string): string {
  return value.trim().toUpperCase().replace(/[\s_.\-()[\]{}%/\\]+/g, '');
}

const IDENTITY_BY_ALIAS = new Map<string, CurveIdentity>();
CURVE_IDENTITIES.forEach((identity) => {
  (ALIASES[identity.canonical] || [identity.canonical]).forEach((alias) => {
    IDENTITY_BY_ALIAS.set(normalizeCurveName(alias), identity);
  });
});

export function recognizeCurve(name: string): CurveIdentity | undefined {
  return IDENTITY_BY_ALIAS.get(normalizeCurveName(name));
}

export function buildCurveCatalog(
  definitions: { sourceKey: string; displayName: string; unit?: string }[],
  rows: ImportedCurveRow[],
): ImportedCurveColumn[] {
  const usedFields = new Set<SweetSpotCurveField>();
  return definitions.map((definition) => {
    const identity = recognizeCurve(definition.displayName);
    const proposed = identity?.evaluationField;
    const mappedField = proposed && !usedFields.has(proposed) ? proposed : undefined;
    if (mappedField) usedFields.add(mappedField);
    const values = rows.map((row) => row[definition.sourceKey]).filter((value): value is number =>
      typeof value === 'number' && Number.isFinite(value));
    const validCount = values.length;
    const missingCount = Math.max(0, rows.length - validCount);
    const conversion = suggestUnitConversion(mappedField, definition.unit, values.slice(0, 200));
    return {
      ...definition,
      recognizedName: identity?.displayName,
      description: identity?.description,
      valueType: 'number',
      mappedField,
      targetUnit: mappedField ? TARGET_UNITS[mappedField] : definition.unit,
      conversion,
      validCount,
      missingCount,
      validRate: rows.length ? validCount / rows.length : 0,
      missingRate: rows.length ? missingCount / rows.length : 0,
      visible: Boolean(mappedField && mappedField !== 'depth'),
    };
  });
}

export const BUILT_IN_PARAMETER_KEYS: BuiltInParameterKey[] = [
  'toc', 'porosity', 'brittleness', 'clayContent',
  'youngModulus', 'poissonRatio', 'minHorizontalStress',
];

export function makeCustomParameterKey(sourceKey: string): `custom_${string}` {
  const safe = normalizeCurveName(sourceKey).replace(/[^A-Z0-9\u4e00-\u9fff]/g, '') || 'CURVE';
  return `custom_${safe}`;
}

export function sampleRows<T>(rows: T[], limit = 2000): T[] {
  if (rows.length <= limit) return rows;
  const sampled: T[] = [];
  for (let index = 0; index < limit; index += 1) {
    sampled.push(rows[Math.round(index * (rows.length - 1) / (limit - 1))]);
  }
  return sampled;
}
